<script setup>
import { useShipStore } from '@/stores/ship'
import { ref, computed, watch } from 'vue'
import FairleadTable from '../fairlead/FairleadTable.vue'
import ShipActionMenu from './ShipRowActionMenu.vue'
import { useAuthorizationStore } from '@/stores/authorization'
import { storeToRefs } from 'pinia/dist/pinia'
import {
  AFTERWARD_PARALLEL_BODY_LENGTH,
  BALLASTED_DRAUGHT,
  BREADTH,
  CAPACITY,
  DEPTH_MAIN_DECK_TO_KEEL,
  ECCENTRICITY,
  FORWARD_PARALLEL_BODY_LENGTH,
  LADEN_DRAUGHT,
  LENGTH_OVERALL,
  NUMBER
} from '@/constants/shipFieldConstants'
import { debounce } from 'lodash'

const store = useShipStore()
const authorizationStore = useAuthorizationStore()

const props = defineProps({
  item: { type: Object, required: true }
})

const { selectedItems } = storeToRefs(store)

const open = ref(false)
const eccentricityLoading = ref(false)

function setCurrentShip() {
  if (props.item.hide) {
    return store.setHide(props.item)
  }
  if (open.value) {
    store.setCurrentItem({})
  } else {
    store.setCurrentItem(props.item)
  }
  open.value = !open.value
  store.setShowLines(props.item, open.value)
}

const canEdit = computed(() =>
  authorizationStore.hasServicePermission({
    authSource: 'ProjectUser',
    allowed: 'change_calculation'
  })
)

const eccentricityDisabled = computed(() => {
  return !canEdit.value || eccentricityLoading.value
})

const eccentricity = computed({
  get: () => {
    return props.item[ECCENTRICITY]
  },
  set: (value) => {
    store.setParameter(props.item, ECCENTRICITY, parseFloat(value))
  }
})

const debounceUpdateEccentricity = debounce(async () => {
  eccentricityLoading.value = true
  await updateShip()
  eccentricityLoading.value = false
}, 1000)

watch(eccentricity, () => {
  debounceUpdateEccentricity()
})

const updateShip = async () => {
  const showLines = props.item.show_lines
  await store.update(props.item)
  store.setShowLines(props.item, showLines)
}

const isSelected = computed({
  get: () => {
    return selectedItems.value?.includes(props.item) || false
  },
  set: (value) => {
    store.updateSelectedItems(value, [props.item])
  }
})
</script>

<template>
  <tr
    v-ripple
    :class="{
      clickable: true,
      'selected-item': isSelected,
      opacity: item.hide
    }"
    @click.stop="setCurrentShip()"
  >
    <td class="pl-1 pr-0">
      <v-btn
        :icon="open ? `mdi-chevron-up` : `mdi-chevron-down`"
        color="#303030"
        :ripple="false"
        variant="text"
        density="compact"
        @click.stop="setCurrentShip()"
        width="28"
        style="margin: auto"
      ></v-btn>
    </td>
    <td class="px-0 text-center">
      <v-checkbox
        color="primary"
        density="compact"
        hide-details
        v-model="isSelected"
        @click.stop=""
        width="28"
        style="margin: auto"
      ></v-checkbox>
    </td>
    <td>{{ item[NUMBER] }}</td>
    <td>{{ item[CAPACITY] }}</td>
    <td class="center">
      <v-text-field
        @click.stop=""
        :disabled="eccentricityDisabled"
        class="input-field"
        v-model="eccentricity"
        type="number"
        density="compact"
        variant="plain"
        hide-details
      ></v-text-field>
    </td>

    <td>{{ item[LENGTH_OVERALL] }}</td>
    <td>{{ item[BREADTH] }}</td>
    <td>{{ item[LADEN_DRAUGHT] }}</td>
    <td>{{ item[BALLASTED_DRAUGHT] }}</td>
    <td>{{ item[DEPTH_MAIN_DECK_TO_KEEL] }}</td>
    <td>{{ item[FORWARD_PARALLEL_BODY_LENGTH] }}</td>
    <td>{{ item[AFTERWARD_PARALLEL_BODY_LENGTH] }}</td>
    <td style="width: 1%">
      <ShipActionMenu :item="item" @update-ship="updateShip"></ShipActionMenu>
    </td>
  </tr>
  <tr v-show="open">
    <td colspan="16" class="pa-0">
      <FairleadTable :ship="item" />
    </td>
  </tr>
</template>

<style scoped>
td {
  text-align: center;
}

.opacity {
  opacity: 0.5;
}

tr:hover {
  background-color: #f5f5f5;
}

.input-field :deep(.v-field__input) {
  padding: 0;
  text-align: center;
  font-size: 14px;
}
</style>
